import { isNewSeries } from './showFields.js';

export function normalizeRatingValue(value) {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return value >= 0 && value <= 10 ? value : null;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) && parsed >= 0 && parsed <= 10 ? parsed : null;
  }

  return null;
}

export function getRatingAtListing(show) {
  if (!show || typeof show !== 'object') {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(show, 'ratingAtListing')) {
    return normalizeRatingValue(show.ratingAtListing);
  }

  return normalizeRatingValue(show.rating);
}

export function getRatingCurrent(show) {
  if (!show || typeof show !== 'object') {
    return null;
  }

  if (Object.prototype.hasOwnProperty.call(show, 'ratingCurrent')) {
    return normalizeRatingValue(show.ratingCurrent) ?? normalizeRatingValue(show.rating);
  }

  return normalizeRatingValue(show.omdbData?.rating ?? show.rating);
}

export function getPrimaryRating(show) {
  return getRatingCurrent(show);
}

// Name IMDb only when IMDb rates the show. A show it hasn't rated carries the
// viewer rating from the release listings (Watchmode or TVMaze).
export function hasImdbRating(show) {
  return normalizeRatingValue(show?.omdbData?.rating) !== null;
}

function getRatingNames(show) {
  return hasImdbRating(show)
    ? { label: 'IMDb', rating: 'IMDb rating', rated: 'on IMDb' }
    : { label: 'Viewer rating', rating: 'viewer rating', rated: 'by viewers' };
}

export function getBestOfRating(show, { mode = 'snapshot' } = {}) {
  if (!show || typeof show !== 'object') {
    return null;
  }

  if (mode === 'current') {
    return getPrimaryRating(show);
  }

  if (Object.prototype.hasOwnProperty.call(show, 'bestOfRating')) {
    return normalizeRatingValue(show.bestOfRating);
  }

  // Compatibility for archive rows created before best-of snapshots existed.
  // Their stored `rating` is stable even when `ratingCurrent` changes.
  return normalizeRatingValue(show.rating) ?? getPrimaryRating(show);
}

export function getRatingCount(show) {
  const value = show?.omdbData?.ratingCount;
  return typeof value === 'number' && value > 0 ? value : null;
}

export function buildAggregateRatingSchema(show) {
  const ratingValue = getPrimaryRating(show);
  const ratingCount = getRatingCount(show);
  if (typeof ratingValue !== 'number' || ratingCount === null) {
    return null;
  }
  return {
    '@type': 'AggregateRating',
    ratingValue,
    bestRating: 10,
    worstRating: 1,
    ratingCount,
  };
}

export function getRatingCurrentCheckedAt(show) {
  return typeof show?.ratingCurrentCheckedAt === 'string' ? show.ratingCurrentCheckedAt : null;
}

export function getRatingDelta(show) {
  const ratingAtListing = getRatingAtListing(show);
  const ratingCurrent = getRatingCurrent(show);

  if (ratingAtListing === null || ratingCurrent === null) {
    return null;
  }

  return ratingCurrent - ratingAtListing;
}

export function hasMeaningfulRatingDifference(show, minimumDelta = 0.1) {
  const delta = getRatingDelta(show);
  return delta !== null && Math.abs(delta) >= minimumDelta;
}

// A season 1 part needs the usual rating, so it never gets the exception's copy.
export function isFirstSeasonShow(show) {
  return isNewSeries(show);
}

function isArchivedListing(show, isArchived = false) {
  return isArchived || Boolean(show?.archivedAt) || String(show?.status || '').toLowerCase() === 'archived';
}

export function getShowCardRatingTooltip(show, { isArchived = false } = {}) {
  const ratingAtListing = getRatingAtListing(show);
  const ratingCurrent = getRatingCurrent(show);
  const isFirstSeason = isFirstSeasonShow(show);
  const archivedListing = isArchivedListing(show, isArchived);
  const names = getRatingNames(show);

  if (isFirstSeason && ratingCurrent === null) {
    return archivedListing
      ? 'Season 1 ratings exception: we included it because it was identified as a new scripted show. New-series IMDb ratings are often missing or unsettled at listing time.'
      : 'Season 1 ratings exception: we include new series that are identified as scripted shows. IMDb ratings are often missing or unsettled before launch.';
  }

  if (isFirstSeason && ratingCurrent < 7) {
    return archivedListing
      ? `Season 1 ratings exception: its ${names.rating} is below 7.0, but we included it because it was identified as a new scripted show. New-series ratings often settle later.`
      : `Season 1 ratings exception: its ${names.rating} is below 7.0, but we include new series that are identified as scripted shows because early ratings often have not settled.`;
  }

  if (ratingCurrent !== null && ratingCurrent < 7 && ratingAtListing !== null && ratingAtListing >= 7) {
    return `Rated ${ratingCurrent.toFixed(1)} ${names.rated} now. It was ${ratingAtListing.toFixed(1)} when we listed it, which met the 7.0 bar at the time.`;
  }

  if (
    ratingCurrent !== null
    && ratingAtListing !== null
    && ratingCurrent >= 7
    && ratingAtListing >= 7
    && Math.abs(ratingCurrent - ratingAtListing) >= 0.1
  ) {
    return `Rated ${ratingCurrent.toFixed(1)} ${names.rated} now. It was ${ratingAtListing.toFixed(1)} when we listed it.`;
  }

  return null;
}

export function getShowDetailRatingState(show, minimumDelta = 0.1) {
  const ratingAtListing = getRatingAtListing(show);
  const ratingCurrent = getRatingCurrent(show);
  const hasMeaningfulDifference = hasMeaningfulRatingDifference(show, minimumDelta);
  const hasConfirmedCurrentRating = normalizeRatingValue(show?.ratingCurrent) !== null;
  const isFirstSeason = isFirstSeasonShow(show);
  const names = getRatingNames(show);

  if (ratingCurrent === null && ratingAtListing === null) {
    return {
      ratingAtListing,
      ratingCurrent,
      hasMeaningfulDifference: false,
      badgeRating: null,
      badgeVariant: 'unrated',
      badgeLabel: 'N/A',
      metaLabel: 'Not yet rated on IMDb',
    };
  }

  if (ratingAtListing === null) {
    return {
      ratingAtListing,
      ratingCurrent,
      hasMeaningfulDifference: false,
      badgeRating: ratingCurrent,
      badgeVariant: ratingCurrent === null ? 'unrated' : 'rating',
      badgeLabel: ratingCurrent === null ? 'N/A' : ratingCurrent.toFixed(1),
      metaLabel: isFirstSeason && ratingCurrent !== null && ratingCurrent < 7
        ? `Debut series exception: included even while its ${names.rating} is below 7.0.`
        : ratingCurrent === null
          ? 'Not yet rated on IMDb'
          : `Current ${names.rating} ${ratingCurrent.toFixed(1)}`,
    };
  }

  if (ratingCurrent === null) {
    return {
      ratingAtListing,
      ratingCurrent,
      hasMeaningfulDifference: false,
      badgeRating: ratingAtListing,
      badgeVariant: 'rating',
      badgeLabel: ratingAtListing.toFixed(1),
      metaLabel: `${names.label} ${ratingAtListing.toFixed(1)} at listing`,
    };
  }

  if (isFirstSeason && ratingCurrent < 7) {
    return {
      ratingAtListing,
      ratingCurrent,
      hasMeaningfulDifference,
      badgeRating: ratingCurrent,
      badgeVariant: 'rating',
      badgeLabel: ratingCurrent.toFixed(1),
      metaLabel: `Debut series exception: included even while its ${names.rating} is below 7.0.`,
    };
  }

  if (!hasMeaningfulDifference) {
    return {
      ratingAtListing,
      ratingCurrent,
      hasMeaningfulDifference: false,
      badgeRating: ratingCurrent ?? ratingAtListing,
      badgeVariant: ratingCurrent === null && ratingAtListing === null ? 'unrated' : 'rating',
      badgeLabel: (ratingCurrent ?? ratingAtListing)?.toFixed(1) ?? 'N/A',
      metaLabel: hasConfirmedCurrentRating
        ? `${names.label} ${(ratingCurrent ?? ratingAtListing).toFixed(1)}`
        : `${names.label} ${ratingAtListing.toFixed(1)} at listing`,
    };
  }

  return {
    ratingAtListing,
    ratingCurrent,
    hasMeaningfulDifference: true,
    badgeRating: ratingCurrent,
    badgeVariant: ratingCurrent === null ? 'unrated' : 'rating',
    badgeLabel: ratingCurrent === null ? 'N/A' : ratingCurrent.toFixed(1),
    metaLabel: ratingCurrent === null
      ? `${names.label} ${ratingAtListing.toFixed(1)} at listing`
      : `${names.label} ${ratingAtListing.toFixed(1)} at listing · Currently ${ratingCurrent.toFixed(1)}`,
  };
}
